/* Buttons utility
 *
 * Normalize input to a simple 5 way nav plus back button
 *
 */
import { Injectable, EventEmitter, PLATFORM_ID, Inject } from '@angular/core';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { Subject } from 'rxjs/Subject';
import { APP_SETTINGS } from '../../../../modules/ng-express-engine/tokens';

// export enum KeyCode {
//     UP = 38,
//     DOWN = 40,
//     LEFT = 37,
//     RIGHT = 39,
//     SELECT = 13,
//     BACKSPACE = 8,
//     ESC = 27,
//     REWIND = 227,
//     PLAY_PAUSE = 179,
//     FAST_FORWARD = 228,
// }
// 
// var _UWPkeyCodeMap = {
//     left: [],
//     right: [],
//     up: [],
//     down: [],
//     accept: [],
//     back: []
// };
// 
// Default gamepad mappings
// _UWPkeyCodeMap.left.push(
//     214, // GamepadLeftThumbstickLeft
//     205, // GamepadDPadLeft
//     140); // NavigationLeft
// _UWPkeyCodeMap.right.push(
//     213, // GamepadLeftThumbstickRight
//     206, // GamepadDPadRight
//     141); // NavigationRight
// _UWPkeyCodeMap.up.push(
//     211, // GamepadLeftThumbstickUp
//     203, // GamepadDPadUp
//     138); // NavigationUp
// _UWPkeyCodeMap.down.push(
//     212, // GamepadLeftThumbstickDown
//     204, // GamepadDPadDown
//     139); // NavigationDown
// _UWPkeyCodeMap.accept.push(
//     142, // NavigationAccept
//     195); // GamepadA
// _UWPkeyCodeMap.back.push(
//     196); // GamepadB


@Injectable()
export class KeysService {
    /**
     * Buttons uses Observables to trigger the button events
     */
    buttonPressed$ = new EventEmitter<number>();
    buttonReleased$ = new EventEmitter<number>();
    buttonRepeat$ = new EventEmitter<number>();

    /**
     * constants for buttons, based onf keyCode for key events:
     */
    
    private BUTTON_INTERVALS = [250, 350, 500];

    // records if the button is currently held down for different interaction on button hold
    private startHeldDown = {};
    private intervalIndex = 0;
    private currentKey = 0;
    private scrollTimerId = 0;
    private suspended = false;
    private buttonIntervals = this.BUTTON_INTERVALS;
    private currentInterval = 0;
    private isBrowser: boolean;
    private KeyCode: any;
    private KEYCODES: any; //KeyCode.BACKSPACE
    /**
     * Buttons provides a simple abstraction for a 5-way nav plus back and media buttons.
     * Buttons trigger events: 'buttonpress', optionally 'buttonrepeat' at a decaying interval, and 'buttonrelease'
     * Only one button at a time can be pressed, other key events are ignored until that button is released.
     * Button codes are based on keyboard keyCodes for simplicity.
     * @return {*}
     * @constructor
     */
    constructor(
        @Inject(PLATFORM_ID) platformId: string,
        @Inject(APP_SETTINGS) private settings: any
    ) {
        this.isBrowser = isPlatformBrowser(platformId);
        this.KeyCode = this.settings.KeyCode;
        this.KEYCODES = [this.KeyCode.ESC, this.KeyCode.SELECT, this.KeyCode.LEFT, this.KeyCode.UP, 
            this.KeyCode.RIGHT, this.KeyCode.DOWN, this.KeyCode.PLAY_PAUSE, this.KeyCode.REWIND, this.KeyCode.FAST_FORWARD];
        if (this.isBrowser) {
            // the system handles the 'back' button on the remote and turns it into a browser back event, so
            // we use the history API to watch for 'popstate' (back button press) events and convert these into
            // button press events for the BACKSPACE key.
            // this is documented more in the AWV app knowledge base
            document.addEventListener("load", function () {
                document.addEventListener("popstate", function (evt) {
                    // if (window.history.state !== "backhandler") {
                    //     // state will be AFTER the 'backhandler' is popped, so we ignore it if it's popping dummy state
                    //     // handle same as BACKSPACE keycode
                    //     if (!this.suspended) {
                    //         console.log("history::back");
                    //         this.handleKeyDown({ type: 'keydown', keyCode: KeyCode.ESC, timeStamp: evt.timeStamp, preventDefault: function () { } });
                    //         this.handleKeyUp({ type: 'keyup', keyCode: KeyCode.ESC, timeStamp: evt.timeStamp, preventDefault: function () { } });
                    //     }
                    //     window.history.pushState("backhandler", null, null);
                    // }
                    return false;
                }.bind(this));
                // if (window.history.state !== "backhandler") {
                //     window.history.pushState("backhandler", null, null); // pushing a dummy state so that popstate is always called
                // }

                return false;
            }.bind(this));

            document.addEventListener("keydown", this.handleKeyDown.bind(this), false);
            document.addEventListener("keyup", this.handleKeyUp.bind(this), false);
        }
    }

    /**
     * reset abandons any subsequent events from the current button press, no more buttonrepeat or buttonrelease events
     * will be generated even if the button remains held down
     */
    reset() {
        this.currentKey = 0;
        if (this.scrollTimerId) {
            window.clearTimeout(this.scrollTimerId);
            this.scrollTimerId = 0;
        }
        this.suspended = false;
    };

    /**
     * resync with browser key state, needed when document loses focus and system keyUp events may get lost
     * if a button is held down the next keyDown event (from auto-repeat) will trigger a 'buttonpress'
     */
    resync() {
        this.reset();
        this.startHeldDown = {};
    };

    /**
     * suspend processing of key events and let them propagate normally, e.g. so that an input
     * element can get text, call reset() to end suspension
     */
    suspend() {
        this.resync();
        this.suspended = true;
    };

    // gets the next delay interval, pass reset=true to start over
    private getButtonInterval(reset) {
        if (reset) {
            this.intervalIndex = this.buttonIntervals.length;
        }

        if (this.intervalIndex > 0) {
            this.intervalIndex--;
        }
        this.currentInterval = this.buttonIntervals[this.intervalIndex];
        return this.currentInterval;
    };

    // browser integration, de-bounce and de-duplicate key events, only allow one button at a time to be handled
    private handleKeyDown(e) {
        var keyCode = e.keyCode;
        //console.log("keypress::" + keyCode)

        let matchedCode = this.checkUWPGamepadKey(keyCode);
        if (matchedCode > -1) keyCode = matchedCode;

        if (!this.suspended && this.KEYCODES.indexOf(keyCode) >= 0) {
            e.preventDefault();
            if (!this.startHeldDown[keyCode]) { // ignore any repeated keyDown events (cleared on keyUp)
                this.startHeldDown[keyCode] = e.timeStamp;
                if (!this.currentKey) {
                    this.currentKey = keyCode;
                    this.scrollTimerId = window.setTimeout(this.doRepeat.bind(this), this.getButtonInterval(true));
                    this.buttonPressed$.emit(keyCode);
                }
            }
        }
    }

    public handleKeyUp(e) {
        var keyCode = e.keyCode;
        let matchedCode = this.checkUWPGamepadKey(keyCode);
        if (matchedCode > -1) keyCode = matchedCode;

        if (!this.suspended && this.KEYCODES.indexOf(keyCode) >= 0) {
            e.preventDefault();
            if (this.startHeldDown[keyCode]) { // ignore any keyUp events that aren't for keys which are down
                this.startHeldDown[keyCode] = 0;

                // only emit release if this handler also got press
                if (this.currentKey === keyCode) {
                    this.currentKey = 0;
                    if (this.scrollTimerId) {
                        window.clearTimeout(this.scrollTimerId);
                        this.scrollTimerId = 0;
                    }
                    this._keyHandler && this._keyHandler.handleKey(keyCode);
                    this.buttonReleased$.emit(keyCode);
                }
            }
        }
    }

    private checkUWPGamepadKey(keyCode): number {
        if (this.settings._UWPkeyCodeMap.up.indexOf(keyCode) !== -1) {
            return this.KeyCode.UP;
        }
        else if (this.settings._UWPkeyCodeMap.down.indexOf(keyCode) !== -1) {
            return this.KeyCode.DOWN;
        }
        else if (this.settings._UWPkeyCodeMap.left.indexOf(keyCode) !== -1) {
            return this.KeyCode.LEFT;
        }
        else if (this.settings._UWPkeyCodeMap.right.indexOf(keyCode) !== -1) {
            return this.KeyCode.RIGHT;
        }
        else if (this.settings._UWPkeyCodeMap.accept.indexOf(keyCode) !== -1) {
            return this.KeyCode.SELECT;
        }
        else if (this.settings._UWPkeyCodeMap.back.indexOf(keyCode) !== -1) {
            return this.KeyCode.ESC;
        }
        return -1;
    }

    private doRepeat() {
        if (!this.suspended && this.currentKey && this.startHeldDown[this.currentKey]) {
            this.scrollTimerId = window.setTimeout(this.doRepeat.bind(this), this.getButtonInterval(false));
            this._keyHandler && this._keyHandler.handleKey(this.currentKey);
            this.buttonRepeat$.emit(this.currentKey);
        }
    }

    private resetButtonIntervals() {
        this.setButtonIntervals(this.BUTTON_INTERVALS);
    }

    private setButtonIntervals(interval) {
        if (this.buttonIntervals !== interval) {
            this.buttonIntervals = interval;
            this.intervalIndex = this.buttonIntervals.length;
        }
    }

    //allows components to register manually for key-handling
    private _keyHandler: any;

    registerKeyHandler(handler) {
        this._keyHandler = handler;
    }
}


// WEBPACK FOOTER //
// ./src/angular-engine/engine/service/ui/input-devices/keys.service.ts